import React, { useEffect, useState } from "react";
import { BACKEND_URL } from "@/lib/backendUrl";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Banknote, CheckCircle2, AlertTriangle, ExternalLink, Copy, User2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${BACKEND_URL}/api`;

/**
 * Settings > Payouts.
 *
 *  1. Handle editor — /tip/<handle> is a DJ's public tip jar URL. We show
 *     it as a copyable link and let the DJ rename it (with live availability
 *     check).
 *  2. Stripe Connect status — Connect Stripe button that opens Stripe-hosted
 *     onboarding in a new tab; a Refresh Status button re-hits
 *     GET /api/stripe/connect/status to pull the latest charges_enabled /
 *     payouts_enabled flags.
 */
export default function PayoutsCard() {
  const { user, refreshUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [handleDraft, setHandleDraft] = useState(user?.handle || "");
  const [handleCheck, setHandleCheck] = useState(null); // null | {available, reason}
  const [savingHandle, setSavingHandle] = useState(false);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const publicTipUrl = user?.handle ? `${origin}/tip/${user.handle}` : "";

  useEffect(() => {
    setHandleDraft(user?.handle || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.handle]);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/stripe/connect/status`);
      setStatus(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load payout status");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadStatus(); /* eslint-disable-next-line */ }, []);

  const startOnboarding = async () => {
    setConnecting(true);
    try {
      const r = await axios.post(`${API}/stripe/connect/onboarding-link`, {});
      window.open(r.data.url, "_blank", "noopener,noreferrer");
      toast.info("Complete onboarding in the Stripe tab, then hit Refresh Status.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start onboarding");
    } finally { setConnecting(false); }
  };

  // Live handle-availability check (debounced by React batching)
  useEffect(() => {
    const h = handleDraft.trim().toLowerCase();
    if (!h || h === user?.handle) { setHandleCheck(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/handles/available`, { params: { handle: h } });
        if (!cancelled) setHandleCheck(r.data);
      } catch { if (!cancelled) setHandleCheck({ available: false, reason: "Check failed" }); }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [handleDraft, user?.handle]);

  const saveHandle = async () => {
    if (!handleCheck?.available) return;
    setSavingHandle(true);
    try {
      await axios.post(`${API}/handles`, { handle: handleDraft.trim().toLowerCase() });
      toast.success("Handle updated.");
      if (refreshUser) await refreshUser();
      setHandleCheck(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save handle");
    } finally { setSavingHandle(false); }
  };

  const copyTipUrl = () => {
    if (!publicTipUrl) return;
    navigator.clipboard.writeText(publicTipUrl);
    toast.success("Tip-jar URL copied.");
  };

  return (
    <div className="rounded-xl border border-[#1E2638] bg-[#121620] p-6 space-y-6" data-testid="payouts-card">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-[#10B981]/10 border border-[#10B981] flex items-center justify-center"><Banknote className="w-5 h-5 text-[#10B981]"/></div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#10B981]">// Payouts</div>
          <div className="font-display font-black text-2xl">Get tipped, get paid.</div>
        </div>
      </div>

      {/* Handle */}
      <div className="space-y-2" data-testid="handle-block">
        <div className="text-xs font-mono uppercase tracking-widest text-[#94A3B8] flex items-center gap-2">
          <User2 className="w-3 h-3"/> Your tip-jar URL
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-mono text-[#94A3B8] whitespace-nowrap">{origin}/tip/</span>
          <Input data-testid="handle-input"
            value={handleDraft}
            onChange={e => setHandleDraft(e.target.value.replace(/[^a-z0-9_-]/gi, "").slice(0, 24))}
            placeholder="dj-katwire"
            className="bg-[#0B0E14] border-[#2A354B] text-white flex-1 font-mono lowercase"/>
        </div>
        {handleCheck && handleDraft !== user?.handle && (
          <div className={`text-[11px] font-mono ${handleCheck.available ? "text-[#10B981]" : "text-[#EF4444]"}`} data-testid="handle-check">
            {handleCheck.available ? "✓ available" : `✗ ${handleCheck.reason || "already taken"}`}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {handleDraft !== user?.handle && (
            <Button data-testid="handle-save"
              onClick={saveHandle}
              disabled={savingHandle || !handleCheck?.available}
              className="bg-[#00F0FF] text-black hover:bg-[#33F3FF] font-black uppercase tracking-widest text-xs">
              {savingHandle ? <Loader2 className="w-3 h-3 animate-spin"/> : "Save handle"}
            </Button>
          )}
          {user?.handle && (
            <Button data-testid="handle-copy" onClick={copyTipUrl} variant="outline"
              className="border-[#2A354B] text-white hover:bg-white/5 font-black uppercase tracking-widest text-xs">
              <Copy className="w-3 h-3 mr-1"/> Copy link
            </Button>
          )}
        </div>
      </div>

      {/* Connect status */}
      <div className="space-y-3 pt-4 border-t border-[#1E2638]" data-testid="connect-block">
        <div className="text-xs font-mono uppercase tracking-widest text-[#94A3B8]">Stripe payouts</div>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-[#94A3B8]"><Loader2 className="w-4 h-4 animate-spin"/> Loading…</div>
        ) : status?.payouts_enabled ? (
          <div className="flex items-center gap-3 p-3 rounded-lg border border-[#10B981]/50 bg-[#10B981]/5" data-testid="connect-enabled">
            <CheckCircle2 className="w-5 h-5 text-[#10B981] flex-shrink-0"/>
            <div className="flex-1">
              <div className="font-bold text-sm">Payouts enabled ✨</div>
              <div className="text-[11px] text-[#94A3B8] font-mono">Tips + merch land in your bank via Stripe · 90 % to you · 10 % platform fee</div>
            </div>
            <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer"
              className="text-xs font-mono uppercase tracking-widest text-[#00F0FF] hover:underline flex items-center gap-1"
              data-testid="stripe-dashboard-link">
              Dashboard <ExternalLink className="w-3 h-3"/>
            </a>
          </div>
        ) : status?.connected ? (
          <div className="p-3 rounded-lg border border-[#FFC107]/50 bg-[#FFC107]/5 space-y-3" data-testid="connect-pending">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-[#FFC107] flex-shrink-0 mt-0.5"/>
              <div className="flex-1">
                <div className="font-bold text-sm">Onboarding in progress</div>
                <div className="text-[11px] text-[#94A3B8] font-mono">Finish the Stripe form to start receiving tips.</div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button data-testid="connect-continue" onClick={startOnboarding} disabled={connecting}
                className="bg-[#00F0FF] text-black hover:bg-[#33F3FF] font-black uppercase tracking-widest text-xs">
                {connecting ? <Loader2 className="w-3 h-3 animate-spin"/> : "Continue onboarding"}
              </Button>
              <Button data-testid="connect-refresh" onClick={loadStatus} variant="outline"
                className="border-[#2A354B] text-white hover:bg-white/5 font-black uppercase tracking-widest text-xs">
                Refresh status
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-4 rounded-lg border border-[#1E2638] bg-[#0B0E14] space-y-3" data-testid="connect-none">
            <div className="text-sm text-[#94A3B8]">
              Connect your own Stripe account and start accepting tips + merch. Each payment splits automatically:
              <span className="text-white font-bold"> 90 % lands in your bank</span>, 10 % supports NeuralDeck.
              Your bank account is <span className="text-white font-bold">only visible to you</span> — Katwire Labs never sees it.
            </div>
            <Button data-testid="connect-start"
              onClick={startOnboarding} disabled={connecting}
              className="w-full bg-[#635BFF] text-white hover:bg-[#7C6FFF] font-black uppercase tracking-widest">
              {connecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Banknote className="w-4 h-4 mr-2"/>}
              Connect Stripe (2-min setup)
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
